import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

const REPORT_WINDOW_SIZE = 20;

function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}

function clean(value) {
  return String(value ?? "").trim();
}

function categoryForIssue(issue) {
  const code = clean(issue?.code);

  if (
    code ===
    "oversized_manuscript_unit"
  ) {
    return "division";
  }

  if (
    code ===
      "missing_manuscript_text" ||
    code ===
      "manuscript_not_complete"
  ) {
    return "manuscript";
  }

  if (
    code ===
    "missing_required_illustration"
  ) {
    return "illustrations";
  }

  if (
    code.includes("cover") ||
    code.includes("spine")
  ) {
    return "cover";
  }

  return "setup";
}

function globalGroupForCategory(
  category,
  isFR
) {
  if (category === "cover") {
    return {
      key: "global-cover",
      label: isFR
        ? "Couverture"
        : "Cover",
      order: -40,
    };
  }

  if (
    category ===
    "illustrations"
  ) {
    return {
      key: "global-illustrations",
      label: isFR
        ? "Illustrations"
        : "Illustrations",
      order: -30,
    };
  }

  return {
    key: "global-setup",
    label: isFR
      ? "Configuration du livre"
      : "Book setup",
    order: -50,
  };
}

function groupIssues(
  issues,
  interiorItems,
  isFR
) {
  const unitLookup = new Map(
    safeArray(interiorItems).map(
      (item, index) => [
        clean(item?.sourceUnitId),
        {
          order: index,
          label:
            clean(
              item?.pageLabel
            ) ||
            clean(
              item?.workingTitle
            ) ||
            (
              isFR
                ? "Page ou double page concernée"
                : "Affected page or spread"
            ),
        },
      ]
    )
  );

  const groups = new Map();

  safeArray(issues).forEach(
    (issue, issueIndex) => {
      const category =
        categoryForIssue(issue);

      const targetId =
        clean(issue?.targetId);

      let key;
      let label;
      let order;
      let kind;

      if (targetId) {
        const unit =
          unitLookup.get(targetId);

        key = `unit-${targetId}`;
        label =
          unit?.label ||
          (
            isFR
              ? "Page ou double page concernée"
              : "Affected page or spread"
          );
        order =
          Number.isFinite(
            unit?.order
          )
            ? unit.order
            : 100000 + issueIndex;
        kind = "unit";
      } else {
        const globalGroup =
          globalGroupForCategory(
            category,
            isFR
          );

        key = globalGroup.key;
        label = globalGroup.label;
        order = globalGroup.order;
        kind = "global";
      }

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          kind,
          targetId,
          label,
          order,
          issues: [],
        });
      }

      groups.get(key).issues.push({
        ...issue,
        reportCategory:
          category,
      });
    }
  );

  return Array.from(
    groups.values()
  ).sort(
    (left, right) =>
      left.order - right.order
  );
}

function messageForIssue(
  issue,
  groupLabel,
  formatIssue
) {
  const message =
    typeof formatIssue ===
    "function"
      ? clean(formatIssue(issue))
      : clean(issue?.code)
          .replaceAll("_", " ");

  const suffix =
    ` — ${groupLabel}`;

  return message.endsWith(suffix)
    ? message.slice(
        0,
        -suffix.length
      )
    : message;
}

export default function ChildrensBookScalablePreflightReport({
  lang = "en",
  issues = [],
  interiorItems = [],
  formatIssue,
  onOpenCoverPlan,
  onOpenManuscriptPlan,
  onOpenIllustrationPlan,
}) {
  const isFR =
    String(lang).toLowerCase() ===
    "fr";

  const proofIssues =
    safeArray(issues);

  const groups = useMemo(
    () =>
      groupIssues(
        proofIssues,
        interiorItems,
        isFR
      ),
    [
      interiorItems,
      isFR,
      proofIssues,
    ]
  );

  const categoryCounts =
    useMemo(
      () =>
        proofIssues.reduce(
          (result, issue) => {
            const category =
              categoryForIssue(
                issue
              );

            result[category] += 1;

            return result;
          },
          {
            manuscript: 0,
            division: 0,
            illustrations: 0,
            cover: 0,
            setup: 0,
          }
        ),
      [proofIssues]
    );

  const affectedUnitCount =
    groups.filter(
      (group) =>
        group.kind === "unit"
    ).length;

  const filters = [
    {
      id: "all",
      label: isFR
        ? "Tous les problèmes"
        : "All issues",
      count: proofIssues.length,
    },
    {
      id: "manuscript",
      label: isFR
        ? "État du manuscrit"
        : "Manuscript status",
      count:
        categoryCounts.manuscript,
    },
    {
      id: "division",
      label: isFR
        ? "À diviser"
        : "Needs division",
      count:
        categoryCounts.division,
    },
    {
      id: "illustrations",
      label: isFR
        ? "Illustrations"
        : "Illustrations",
      count:
        categoryCounts.illustrations,
    },
    {
      id: "cover",
      label: isFR
        ? "Couverture"
        : "Cover",
      count:
        categoryCounts.cover,
    },
    {
      id: "setup",
      label: isFR
        ? "Configuration"
        : "Book setup",
      count:
        categoryCounts.setup,
    },
  ].filter(
    (filter) =>
      filter.id === "all" ||
      filter.count > 0
  );

  const [
    activeFilter,
    setActiveFilter,
  ] = useState("all");

  const [
    windowStart,
    setWindowStart,
  ] = useState(0);

  const filteredGroups =
    useMemo(
      () =>
        groups
          .map((group) => {
            if (
              activeFilter ===
              "all"
            ) {
              return group;
            }

            return {
              ...group,
              issues:
                group.issues.filter(
                  (issue) =>
                    issue.reportCategory ===
                    activeFilter
                ),
            };
          })
          .filter(
            (group) =>
              group.issues.length > 0
          ),
      [
        activeFilter,
        groups,
      ]
    );

  useEffect(() => {
    setWindowStart(0);
  }, [activeFilter]);

  const maximumWindowStart =
    Math.max(
      0,
      Math.floor(
        Math.max(
          filteredGroups.length - 1,
          0
        ) /
          REPORT_WINDOW_SIZE
      ) * REPORT_WINDOW_SIZE
    );

  useEffect(() => {
    setWindowStart(
      (current) =>
        Math.min(
          current,
          maximumWindowStart
        )
    );
  }, [maximumWindowStart]);

  const windowEnd =
    Math.min(
      filteredGroups.length,
      windowStart +
        REPORT_WINDOW_SIZE
    );

  const visibleGroups =
    filteredGroups.slice(
      windowStart,
      windowEnd
    );

  const rangeCount =
    Math.max(
      1,
      Math.ceil(
        filteredGroups.length /
          REPORT_WINDOW_SIZE
      )
    );

  function actionsForGroup(
    group
  ) {
    const categories =
      new Set(
        group.issues.map(
          (issue) =>
            issue.reportCategory
        )
      );

    const actions = [];

    if (
      (
        categories.has(
          "manuscript"
        ) ||
        categories.has(
          "division"
        )
      ) &&
      typeof onOpenManuscriptPlan ===
        "function"
    ) {
      actions.push({
        key: "manuscript",
        label: isFR
          ? "Corriger le manuscrit"
          : "Correct manuscript",
        onClick: () =>
          onOpenManuscriptPlan({
            unitId:
              group.targetId || "",
          }),
      });
    }

    if (
      categories.has(
        "illustrations"
      ) &&
      typeof onOpenIllustrationPlan ===
        "function"
    ) {
      actions.push({
        key: "illustrations",
        label: isFR
          ? "Corriger les illustrations"
          : "Correct illustrations",
        onClick: () =>
          onOpenIllustrationPlan({
            unitId:
              group.targetId || "",
          }),
      });
    }

    if (
      categories.has("cover") &&
      typeof onOpenCoverPlan ===
        "function"
    ) {
      actions.push({
        key: "cover",
        label: isFR
          ? "Corriger la couverture"
          : "Correct cover",
        onClick: () =>
          onOpenCoverPlan({
            unitId:
              group.targetId || "",
          }),
      });
    }

    return actions;
  }

  const summaryItems = [
    {
      label: isFR
        ? "Pages / doubles pages concernées"
        : "Affected pages / spreads",
      value: affectedUnitCount,
    },
    {
      label: isFR
        ? "État du manuscrit"
        : "Manuscript status",
      value:
        categoryCounts.manuscript,
    },
    {
      label: isFR
        ? "Pages / doubles pages à diviser"
        : "Pages / spreads needing division",
      value:
        categoryCounts.division,
    },
    {
      label: isFR
        ? "Illustrations"
        : "Illustrations",
      value:
        categoryCounts.illustrations,
    },
    {
      label: isFR
        ? "Couverture et configuration"
        : "Cover and setup",
      value:
        categoryCounts.cover +
        categoryCounts.setup,
    },
  ];

  return (
    <section
      className="ns-card"
      aria-label={
        isFR
          ? "Rapport de contrôle"
          : "Preflight report"
      }
      style={{
        padding: 16,
        borderRadius: 16,
        marginBottom: 18,
      }}
    >
      <h3
        className="ns-card-title"
        style={{
          margin: "0 0 6px",
        }}
      >
        {isFR
          ? "Rapport de contrôle"
          : "Preflight Report"}
      </h3>

      <p
        className="ns-card-meta"
        style={{
          margin: "0 0 14px",
          lineHeight: 1.55,
        }}
      >
        {isFR
          ? "Les problèmes sont regroupés par page ou double page afin que les livres volumineux restent faciles à examiner."
          : "Issues are grouped by affected page or spread so that large books remain manageable to review."}
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {summaryItems.map(
          (item) => (
            <div
              key={item.label}
              className="ns-nbb-inner-panel"
              style={{
                padding: 12,
                borderRadius: 11,
                background: "#f8fafc",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  marginBottom: 4,
                }}
              >
                {item.label}
              </div>

              <strong>
                {item.value}
              </strong>
            </div>
          )
        )}
      </div>

      <div
        aria-label={
          isFR
            ? "Filtres du rapport"
            : "Preflight report filters"
        }
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        {filters.map(
          (filter) => (
            <button
              key={filter.id}
              className={
                activeFilter ===
                filter.id
                  ? "ns-button ns-button-primary"
                  : "ns-button ns-button-outline"
              }
              type="button"
              aria-pressed={
                activeFilter ===
                filter.id
              }
              onClick={() =>
                setActiveFilter(
                  filter.id
                )
              }
            >
              {filter.label} (
              {filter.count})
            </button>
          )
        )}
      </div>

      <div
        aria-live="polite"
        className="ns-card-meta"
        style={{
          marginBottom: 12,
        }}
      >
        {filteredGroups.length > 0
          ? (
              isFR
                ? `Affichage ${windowStart + 1}–${windowEnd} sur ${filteredGroups.length} entrée(s) concernée(s)`
                : `Showing ${windowStart + 1}–${windowEnd} of ${filteredGroups.length} affected entries`
            )
          : (
              isFR
                ? "Aucun problème dans ce filtre."
                : "No issues match this filter."
            )}
      </div>

      <div
        style={{
          display: "grid",
          gap: 10,
        }}
      >
        {visibleGroups.map(
          (group) => {
            const actions =
              actionsForGroup(group);

            const issueCount =
              group.issues.length;

            return (
              <section
                key={group.key}
                className="ns-nbb-inner-panel"
                aria-label={
                  isFR
                    ? `Problèmes de contrôle — ${group.label}`
                    : `${group.label} preflight issues`
                }
                style={{
                  padding: 14,
                  borderRadius: 12,
                  background: "#fef2f2",
                  border:
                    "1px solid rgba(185,28,28,0.24)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems:
                      "flex-start",
                    gap: 12,
                    flexWrap: "wrap",
                    marginBottom: 9,
                  }}
                >
                  <div>
                    <h4
                      className="ns-card-title"
                      style={{
                        margin: "0 0 4px",
                      }}
                    >
                      {group.label}
                    </h4>

                    <div
                      className="ns-card-meta"
                    >
                      {isFR
                        ? `${issueCount} problème${issueCount === 1 ? "" : "s"}`
                        : `${issueCount} issue${issueCount === 1 ? "" : "s"}`}
                    </div>
                  </div>

                  {actions.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      {actions.map(
                        (action) => (
                          <button
                            key={
                              action.key
                            }
                            className="ns-button ns-button-outline"
                            type="button"
                            aria-label={`${action.label} — ${group.label}`}
                            onClick={
                              action.onClick
                            }
                          >
                            {action.label}
                          </button>
                        )
                      )}
                    </div>
                  ) : null}
                </div>

                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 20,
                    display: "grid",
                    gap: 7,
                  }}
                >
                  {group.issues.map(
                    (
                      issue,
                      index
                    ) => (
                      <li
                        key={`${issue?.code || "issue"}-${index}`}
                      >
                        {messageForIssue(
                          issue,
                          group.label,
                          formatIssue
                        )}
                      </li>
                    )
                  )}
                </ul>
              </section>
            );
          }
        )}
      </div>

      {rangeCount > 1 ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",
            gap: 10,
            flexWrap: "wrap",
            marginTop: 16,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={
              windowStart === 0
            }
            onClick={() =>
              setWindowStart(
                Math.max(
                  0,
                  windowStart -
                    REPORT_WINDOW_SIZE
                )
              )
            }
          >
            {isFR
              ? "20 précédentes"
              : "Previous 20"}
          </button>

          <select
            className="ns-input"
            aria-label={
              isFR
                ? "Plage du rapport de contrôle"
                : "Preflight report range"
            }
            value={windowStart}
            onChange={(event) =>
              setWindowStart(
                Number(
                  event.target.value
                ) || 0
              )
            }
          >
            {Array.from(
              {
                length:
                  rangeCount,
              },
              (_, rangeIndex) => {
                const start =
                  rangeIndex *
                  REPORT_WINDOW_SIZE;

                const end =
                  Math.min(
                    filteredGroups.length,
                    start +
                      REPORT_WINDOW_SIZE
                  );

                return (
                  <option
                    key={start}
                    value={start}
                  >
                    {isFR
                      ? `Plage ${rangeIndex + 1} · Entrées ${start + 1}–${end}`
                      : `Range ${rangeIndex + 1} · Entries ${start + 1}–${end}`}
                  </option>
                );
              }
            )}
          </select>

          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={
              windowEnd >=
              filteredGroups.length
            }
            onClick={() =>
              setWindowStart(
                Math.min(
                  maximumWindowStart,
                  windowStart +
                    REPORT_WINDOW_SIZE
                )
              )
            }
          >
            {isFR
              ? "20 suivantes"
              : "Next 20"}
          </button>
        </div>
      ) : null}
    </section>
  );
}
